import { Link } from "wouter";
import { Calendar, User, Zap, Bookmark, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Article, categories } from "@shared/schema";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { australianStates } from "@shared/suburbs";
import { normalizeImageUrl } from "@/lib/image-url";

interface NewsCardProps {
  article: Article;
  featured?: boolean;
}

export function NewsCard({ article, featured = false }: NewsCardProps) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const category = categories.find((c) => c.id === article.category);
  const categoryColor = {
    australia: "bg-chart-2 text-white",
    korea: "bg-chart-3 text-white",
    community: "bg-chart-4 text-white",
    business: "bg-chart-5 text-white",
    culture: "bg-primary text-primary-foreground",
    investment: "bg-chart-1 text-white",
    immigration: "bg-amber-500 text-white",
  }[article.category] || "bg-muted text-muted-foreground";

  // Get state info for location badge
  const stateInfo = article.region && article.region !== "ALL"
    ? australianStates.find(s => s.id === article.region)
    : null;

  // 찜하기 상태 확인
  const { data: bookmarkData } = useQuery<{ isBookmarked: boolean; bookmarkId?: string }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "article", article.id],
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "article",
        itemId: String(article.id ?? ""),
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user,
  });

  const isBookmarked = bookmarkData?.isBookmarked || false;

  // 찜하기 추가
  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      // NOTE: apiRequest is (method, url, data) - this previously passed
      // (url, method, data), which made fetch() treat "POST" as the URL and
      // "/api/bookmarks" as the HTTP method, silently breaking article
      // bookmarking everywhere this card is used.
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "article",
        itemId: article.id,
        itemTitle: article.titleKo || article.title,
        itemImageUrl: article.imageUrl || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "article", article.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({
        title: "찜하기 완료",
        description: "마이페이지에서 확인하실 수 있습니다",
      });
    },
    onError: (error: any) => {
      toast({
        title: "찜하기 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 찜하기 삭제
  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!bookmarkData?.bookmarkId) throw new Error("북마크 ID를 찾을 수 없습니다");
      return await apiRequest("DELETE", `/api/bookmarks/${bookmarkData.bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "article", article.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({
        title: "찜하기 취소",
        description: "찜하기가 해제되었습니다",
      });
    },
    onError: (error: any) => {
      toast({
        title: "찜하기 취소 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast({
        title: "로그인이 필요합니다",
        description: "찜하기 기능을 사용하려면 로그인해주세요",
        variant: "destructive",
      });
      return;
    }

    if (isBookmarked) {
      removeBookmarkMutation.mutate();
    } else {
      addBookmarkMutation.mutate();
    }
  };

  const isPending = addBookmarkMutation.isPending || removeBookmarkMutation.isPending;

  return (
    <div className="relative" data-testid={`card-article-${article.id}`}>
      <Link href={`/article/${article.id}`}>
        <Card className="overflow-hidden hover-elevate transition-all duration-200 h-full">
          <div className="flex gap-3 p-3">
            {/* 80x80 Thumbnail */}
            {article.imageUrl && (
              <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                <img
                  src={normalizeImageUrl(article.imageUrl) || article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
              {/* Badges - 카테고리 제거, 속보와 지역만 표시 */}
              <div className="flex items-center gap-2 flex-wrap">
                {stateInfo && (
                  <Badge variant="outline" className="w-fit text-xs no-default-hover-elevate no-default-active-elevate" data-testid={`badge-region-${article.id}`}>
                    <MapPin className="h-3 w-3 mr-1" />
                    {stateInfo.nameKo}
                  </Badge>
                )}
                {article.isBreaking && (
                  <Badge variant="destructive" className="w-fit text-xs no-default-hover-elevate no-default-active-elevate" data-testid="badge-breaking">
                    <Zap className="h-3 w-3 mr-1 fill-current" />
                    속보
                  </Badge>
                )}
              </div>

              {/* Title (2 lines) */}
              <h3 className="font-semibold text-base line-clamp-2">
                {article.titleKo || article.title}
              </h3>

              {/* Summary (1 line) */}
              <p className="text-sm text-muted-foreground line-clamp-1">
                {article.summaryKo || article.summary}
              </p>

              {/* Meta */}
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground pt-1">
                <div className="flex items-center gap-3">
                  {article.author && (
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{article.author}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>{format(new Date(article.publishedAt), "MM.dd", { locale: ko })}</span>
                  </div>
                  {category && (
                    <>
                      <span className="text-muted-foreground/60">·</span>
                      <span className="text-muted-foreground">
                        {category.label}
                      </span>
                    </>
                  )}
                </div>
                {/* 찜하기 버튼 - 오른쪽 아래 */}
                {isAuthenticated && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-6 w-6 ${isBookmarked ? 'text-primary' : 'text-muted-foreground'}`}
                    onClick={handleBookmarkToggle}
                    disabled={isPending}
                    data-testid={`button-bookmark-${article.id}`}
                  >
                    <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-current' : ''}`} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </div>
  );
}
